export const CURRENT_PERSON_ID = 'me'

export const enum GRANT_TYPE {
  password = 'password',
  refreshToken = 'refresh_token',
}

export const enum ENTRY_POINTS {
  people = 'people',
  relations = 'relations',
  professions = 'professions',
  products = 'products',
  budgetItems = 'budget_items',
  portfolios = 'portfolios',
  circuits = 'circuits',
  tokens = 'tokens',
  pedagogicalContents = 'pedagogical_contents',
  calculator = 'calculator',
  career = 'career',
  investorProfile = 'investor_profile',
  retirementFunds = 'retirement_funds',
  retirementTrimestersBuyback = 'retirement_trimesters_buyback',
  shortCircuit = 'short-circuit',
  survey = 'survey',
}

export const LEVERS = {
  agriForest: 'agri_foret',
  bareProperty: 'bare_property',
  beneficiaryClause: 'beneficiary_clause',
  dateOfRetirement: 'date_of_retirement',
  ddv: 'ddv',
  deposit: 'deposit',
  donation: 'donation',
  financialContract: 'financial_contract',
  financialNiche: 'financial_niche',
  financialInvestment: 'financial_investment',
  funeralCosts: 'funeral_costs',
  historicalMonument: 'historical_monument',
  lifeInsurance: 'life_assurance',
  lmnp: 'lmnp',
  loanCapacity: 'loan_capacity',
  mainResidence: 'main_residence',
  malraux: 'malraux',
  maritalClause: 'marital_clause',
  maritalStatus: 'marital_status',
  matrimonialRegime: 'matrimonial_regime',
  portfoliosRestructurer: 'portfolios_restructurer',
  protectionSubscription: 'protection_subscription',
  protectionForm: 'protection_form',
  rentalProperty: 'rental_property',
  retirementHypotheses: 'retirement_hypotheses',
  scpi: 'scpi',
  subscription: 'subscription',
  successionHypotheses: 'succession_hypotheses',
  trimestersBuyback: 'trimesters_buyback',
  buyBackQuarters: 'trimesters_buyback',
  reallocation: 'reallocation',
} as const

export type Lever = ValueOf<typeof LEVERS>

export const CIRCUITS = {
  none: 'none',
  patrimonialGrowth: 'patrimonial_growth', // DMP
  retirement: 'retirement',
  shortCircuit: 'fast_circuit', // short circuit but named "fast circuit" in back-end
  protection: 'protection', // pension capital
  inheritance: 'inheritance', // Transmission
  taxation: 'taxation', // Fiscalité
  propertyInvestment: 'property_investment',
  deathBenefit: 'deathBenefit',
  reallocation: 'reallocation',
  minor: 'minor',
} as const

export type Circuits = ValueOf<typeof CIRCUITS>

export const BACKEND_CIRCUITS_MAPPER = {
  [CIRCUITS.patrimonialGrowth]: CIRCUITS.patrimonialGrowth,
  [CIRCUITS.retirement]: CIRCUITS.retirement,
  [CIRCUITS.shortCircuit]: CIRCUITS.shortCircuit,
  [CIRCUITS.protection]: CIRCUITS.protection,
  [CIRCUITS.inheritance]: CIRCUITS.inheritance,
  [CIRCUITS.taxation]: CIRCUITS.taxation,
  [CIRCUITS.deathBenefit]: CIRCUITS.deathBenefit,
  [CIRCUITS.propertyInvestment]: CIRCUITS.propertyInvestment,
  [CIRCUITS.reallocation]: CIRCUITS.reallocation,
  [CIRCUITS.minor]: CIRCUITS.patrimonialGrowth,
  [CIRCUITS.none]: CIRCUITS.none,
} as const satisfies Record<Circuits, string>

export const LEVERS_BY_CIRCUIT = {
  [CIRCUITS.none]: [],
  [CIRCUITS.inheritance]: [
    LEVERS.maritalStatus,
    LEVERS.matrimonialRegime,
    LEVERS.maritalClause,
    LEVERS.successionHypotheses,
    LEVERS.donation,
    LEVERS.agriForest,
    LEVERS.lifeInsurance,
    LEVERS.beneficiaryClause,
    LEVERS.ddv,
  ],
  [CIRCUITS.retirement]: [
    LEVERS.financialContract,
    LEVERS.dateOfRetirement,
    LEVERS.retirementHypotheses,
    LEVERS.trimestersBuyback,
  ],
  [CIRCUITS.propertyInvestment]: [
    LEVERS.loanCapacity,
    LEVERS.mainResidence,
    LEVERS.rentalProperty,
    LEVERS.bareProperty,
    LEVERS.scpi,
    LEVERS.lmnp,
    LEVERS.financialNiche,
    LEVERS.malraux,
    LEVERS.historicalMonument,
  ],
  [CIRCUITS.patrimonialGrowth]: [LEVERS.portfoliosRestructurer],
  [CIRCUITS.deathBenefit]: [],
  [CIRCUITS.shortCircuit]: [LEVERS.subscription, LEVERS.deposit],
  [CIRCUITS.protection]: [LEVERS.protectionForm],
  [CIRCUITS.reallocation]: [LEVERS.reallocation],
  [CIRCUITS.taxation]: [],
  [CIRCUITS.minor]: [],
} as const

export const enum SUB_RESOURCES {
  configurations = 'configurations',
  csps = 'csps',
}

export type Payload = {
  type: string
  attributes?: Record<string, unknown>
}
